import { CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { $convertToMarkdownString, TRANSFORMERS } from "@lexical/markdown";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import TextFormatIcon from "@mui/icons-material/TextFormat";
import { styled } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import Tooltip from "@mui/material/Tooltip";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import type { Klass, LexicalNode, LexicalNodeReplacement } from "lexical";
import { CLEAR_EDITOR_COMMAND, EditorState, LexicalEditor } from "lexical";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import { addMessage } from "messaging/_stores/messages-redux-slice";
import AutoLinkPlugin from "messaging/_views/components/editor/plugins/auto-link-plugin";
import {
  SEND_MESSAGE_COMMAND,
  SubmitMessagePlugin,
} from "messaging/_views/components/editor/plugins/submit-message-plugin";
import ToolbarPlugin from "messaging/_views/components/editor/plugins/toolbar-plugin";
import SubjectButton from "messaging/_views/components/message-subject-button";
import Attachment, { FileAttachment } from "messaging/attachments/_views/message-attachment-editor";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import Forum from "messaging/forums/forum";
import Message from "messaging/message";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useSession } from "users/__session/session-context";
import "./message-editor.css";

const ActionsContainer = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  justifyContent: "space-between",
  marginTop: theme.spacing(0.5),
}));
const OptionButtons = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
}));
const OptionsButtonsContainer = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
}));
const EditorWrapper = styled("div")(() => ({
  position: "relative",
}));
const SendButton = styled(LoadingButton)(({ theme }) => ({
  padding: theme.spacing(1, 0.5),
  position: "sticky",
  top: 0,
}));
const AttachButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const AttachmentInput = styled("input")(() => ({
  display: "none",
}));

const PlaceholderText = () => {
  return <div className="placeholder-text">Type your message here...</div>;
};

interface LexicalTheme {
  root: string;
  quote: string;
  link: string;
  text: {
    bold: string;
    code: string;
    italic: string;
    strikethrough: string;
    subscript: string;
    superscript: string;
    underline: string;
    underlineStrikethrough: string;
  };
}

interface LexicalConfig {
  namespace: string;
  theme: LexicalTheme;
  nodes: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>;
  onError: (error: Error, editor: LexicalEditor) => void;
}

const lexicalConfig: LexicalConfig = {
  namespace: "lexical-editor",
  theme: {
    root: "editor-root",
    quote: "editor-quote",
    link: "editor-link",
    text: {
      bold: "editor-textBold",
      code: "editor-textCode",
      italic: "editor-textItalic",
      strikethrough: "editor-textStrikethrough",
      subscript: "editor-textSubscript",
      superscript: "editor-textSuperscript",
      underline: "editor-textUnderline",
      underlineStrikethrough: "editor-textUnderlineStrikethrough",
    },
  },
  nodes: [HorizontalRuleNode, CodeNode, HeadingNode, AutoLinkNode, ListNode, ListItemNode, QuoteNode, LinkNode],
  onError: (error: Error, editor: LexicalEditor) => console.error(error, editor),
};

type MessageEditorProps = {
  forum: Forum | undefined;
  autoFocus?: boolean;
  locked?: boolean;
  onFormattingToggled: () => void;
  onAttachmentAdded: (attachment: File) => void;
  onMessageSent: (message: Message) => void;
};

export default function MessageEditor(props: Readonly<MessageEditorProps>) {
  const [isSending, setIsSending] = React.useState(false);
  const [messageText, setMessageText] = React.useState<string>("");
  const [subject, setSubject] = React.useState<string | undefined>("");
  const [attachment, setAttachment] = React.useState<FileAttachment | undefined>();

  const [showToolbar, setShowToolbar] = React.useState(false);
  const editorRef = React.useRef<LexicalEditor>(null);
  const hiddenAttachmentFileInput = React.useRef<HTMLInputElement>(null);
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const canSendMessage = (): boolean => {
    return !isDisabled() && ((messageText && messageText.length > 0) || attachment !== undefined);
  };

  const isDisabled = (): boolean => {
    return props.locked || !props.forum || isSending || !session.user;
  };

  const sendMessage = async (message: Message, editor?: LexicalEditor) => {
    if (!canSendMessage()) return;

    setIsSending(true);
    try {
      const messagingService = new MessagingAPIService(session);
      message.publish();
      const newMessage = await messagingService.createMessage(message);
      dispatch(addMessage(newMessage));
      props.onMessageSent(message);
    } catch (error) {
      console.error(`Unable to send message: ${error}`);
      enqueueSnackbar("Failed to send message. Please try again.", {
        variant: "error",
      });
    } finally {
      editor?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      setMessageText("");
      setSubject(undefined);
      setAttachment(undefined);
      setIsSending(false);
    }
  };

  const draftMessage = (messageBody?: string) => {
    if (!props.forum || !session.user || !canSendMessage()) return;
    const attachmentList = attachment ? [attachment.file] : [];
    try {
      return Message.draft({
        id: Guid.generate(),
        created: new AHDate(),
        forum: props.forum,
        sender: session.user,
        content: messageBody ?? messageText,
        subject: subject,
        attachments: attachmentList,
      });
    } catch (error) {
      console.error(`Unable to draft message: ${error}`);
    }
  };

  const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    setAttachment({
      file: files[0],
      fileType: files[0].type.split("/")[1],
      url: URL.createObjectURL(files[0]),
    });

    event.target.value = "";
    props?.onAttachmentAdded(files[0]);
  };

  const handleSendButtonClick = () => {
    if (!canSendMessage()) return;
    const draft = draftMessage();
    if (!draft) return;
    editorRef.current?.dispatchCommand(SEND_MESSAGE_COMMAND, draft);
  };

  const constructMessage = (): Message | undefined => {
    if (!messageText || !session.user?.id || !props.forum) return;
    const message = Message.create({
      forum: props.forum,
      content: messageText,
      senderId: session.user.id,
      subject: subject,
    });

    if (!attachment) return message;
    const attachmentFile = new MessageFileInfo({
      id: Guid.generate(),
      name: attachment.file.name,
      fileSize: attachment.file.size,
      mimeType: attachment.fileType,
      createdDate: new AHDate(),
    });
    message.addAttachments([attachmentFile]);

    return message;
  };

  return (
    <React.Fragment>
      <AttachmentInput
        type="file"
        hidden={true}
        multiple={false}
        ref={hiddenAttachmentFileInput}
        accept={Config.acceptedAttachmentFormats.join(", ")}
        onChange={handleAttachmentUpload}
        style={{ display: "none" }}
      />
      <EditorWrapper>
        <LexicalComposer initialConfig={lexicalConfig}>
          <div className="editor-container">
            {showToolbar && <ToolbarPlugin />}
            <Tooltip title="Add Subject">
              <span>
                <SubjectButton
                  disabled={isDisabled()}
                  forceReset={subject === undefined}
                  onSubjectChanged={(subject: string) => setSubject(subject)}
                />
              </span>
            </Tooltip>
            <div className="editor-inner">
              {showToolbar ? (
                <RichTextPlugin
                  contentEditable={<ContentEditable contentEditable={!isDisabled()} />}
                  placeholder={<PlaceholderText />}
                  ErrorBoundary={LexicalErrorBoundary}
                />
              ) : (
                <PlainTextPlugin
                  contentEditable={<ContentEditable contentEditable={!isDisabled()} />}
                  placeholder={<PlaceholderText />}
                  ErrorBoundary={LexicalErrorBoundary}
                />
              )}
              <SubmitMessagePlugin
                message={constructMessage()}
                listenForEnterKey={!showToolbar && canSendMessage()}
                listenForSubmitButton={canSendMessage()}
                onSend={sendMessage}
              />
              <EditorRefPlugin editorRef={editorRef} />
              <HistoryPlugin />
              <AutoFocusPlugin />
              <AutoLinkPlugin />
              <ClearEditorPlugin />
              <ListPlugin />
              <LinkPlugin />
              <OnChangePlugin
                onChange={(state: EditorState, editor: LexicalEditor) => {
                  editor.update(() => {
                    const markdown = $convertToMarkdownString(TRANSFORMERS);
                    setMessageText(markdown);
                    if (markdown.length < 1 && attachment === undefined) return;
                  });
                }}
              />
              <SendButton
                color="primary"
                size="large"
                endIcon={<SendIcon />}
                loading={isSending}
                disabled={!canSendMessage()}
                onClick={handleSendButtonClick}
              >
                {`Send${isSending ? "ing..." : ""}`}
              </SendButton>
            </div>
          </div>
        </LexicalComposer>
      </EditorWrapper>
      <ActionsContainer>
        <OptionButtons>
          <OptionsButtonsContainer>
            <Tooltip title="Toggle Formatting">
              <span>
                <ToggleButton
                  disabled={isDisabled()}
                  size="small"
                  selected={showToolbar}
                  style={{ border: 0 }}
                  value={showToolbar}
                  onChange={async () => {
                    setShowToolbar(!showToolbar);
                    props.onFormattingToggled();
                  }}
                >
                  <TextFormatIcon />
                </ToggleButton>
              </span>
            </Tooltip>
            <Tooltip title="Attach File">
              <span>
                <AttachButton
                  size="small"
                  disabled={attachment !== undefined || isDisabled()}
                  onClick={() => hiddenAttachmentFileInput?.current?.click()}
                >
                  <AttachFileIcon />
                </AttachButton>
              </span>
            </Tooltip>
            {attachment?.url && (
              <Attachment attachment={attachment} onAttachmentRemoved={() => setAttachment(undefined)} />
            )}
          </OptionsButtonsContainer>
        </OptionButtons>
      </ActionsContainer>
    </React.Fragment>
  );
}
